import { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import toast, { Toaster } from "react-hot-toast";
import { Search, Users, Shield, UserCheck, GraduationCap, Trash2 } from "lucide-react";

const ROLES = ["all","superadmin","admin","member","volunteer","intern"];
const ROLE_COLORS = {
  superadmin:"#f59e0b", admin:"#3b82f6", member:"#22c55e", volunteer:"#a855f7", intern:"#ec4899"
};

export default function Members() {
  const { userProfile } = useAuth();
  const isSuperAdmin = userProfile?.role === "superadmin";

  const [members,    setMembers]    = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [search,     setSearch]     = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [loading,    setLoading]    = useState(true);
  const [updating,   setUpdating]   = useState("");

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDocs(query(collection(db, "users"), orderBy("name")));
        const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setMembers(data);
        setFiltered(data);
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  useEffect(() => {
    let res = members;
    if (roleFilter !== "all") res = res.filter(m => m.role === roleFilter);
    if (search) res = res.filter(m =>
      m.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.email?.toLowerCase().includes(search.toLowerCase()) ||
      m.memberId?.toLowerCase().includes(search.toLowerCase()) ||
      m.phone?.includes(search)
    );
    setFiltered(res);
  }, [search, roleFilter, members]);

  async function changeStatus(uid, status) {
    setUpdating(uid);
    try {
      await updateDoc(doc(db, "users", uid), { status });
      setMembers(p => p.map(m => m.id === uid ? { ...m, status } : m));
      toast.success(`Status updated to ${status}`);
    } catch(e) {
      console.error(e);
      toast.error("Failed to update status");
    } finally { setUpdating(""); }
  }

  async function changeRole(uid, role) {
    setUpdating(uid + role);
    try {
      await updateDoc(doc(db, "users", uid), { role });
      setMembers(p => p.map(m => m.id === uid ? { ...m, role } : m));
      toast.success(`Role updated to ${role}`);
    } catch(e) {
      console.error(e);
      toast.error("Failed to update role");
    } finally { setUpdating(""); }
  }

  async function handleDelete(uid, name) {
    const confirmed = window.confirm(
      `⚠️ DELETE ${name}?\n\nThis will permanently remove their profile from the database.\nThis action CANNOT be undone!`
    );
    if (!confirmed) return;
    setUpdating("del" + uid);
    try {
      await deleteDoc(doc(db, "users", uid));
      setMembers(p => p.filter(m => m.id !== uid));
      toast.success(`${name} deleted successfully`);
    } catch(e) {
      console.error(e);
      toast.error("Failed to delete member");
    } finally { setUpdating(""); }
  }

  const counts = {
    total:     members.length,
    admin:     members.filter(m => m.role === "admin" || m.role === "superadmin").length,
    member:    members.filter(m => m.role === "member").length,
    volunteer: members.filter(m => m.role === "volunteer").length,
    intern:    members.filter(m => m.role === "intern").length,
  };

  return (
    <div>
      <Toaster position="top-center"/>

      {/* Stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:12,marginBottom:20}}>
        {[
          {label:"Total",     value:counts.total,     color:"#f59e0b", icon:Users},
          {label:"Admins",    value:counts.admin,     color:"#3b82f6", icon:Shield},
          {label:"Members",   value:counts.member,    color:"#22c55e", icon:UserCheck},
          {label:"Volunteers",value:counts.volunteer, color:"#a855f7", icon:Users},
          {label:"Interns",   value:counts.intern,    color:"#ec4899", icon:GraduationCap},
        ].map(({label,value,color,icon:Icon}) => (
          <div key={label} style={{background:"#111009",border:"1px solid rgba(255,255,255,0.07)",borderRadius:12,padding:14}}>
            <div style={{width:30,height:30,borderRadius:8,background:`${color}18`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:8}}>
              <Icon size={15} color={color}/>
            </div>
            <div style={{fontSize:22,fontWeight:800,color,fontFamily:"'Playfair Display',serif"}}>{value}</div>
            <div style={{fontSize:11,color:"#52504d",marginTop:2}}>{label}</div>
          </div>
        ))}
      </div>

      {/* Search & Filter */}
      <div style={{background:"#111009",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,padding:16,marginBottom:16}}>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <div style={{flex:1,minWidth:200,position:"relative"}}>
            <Search size={15} color="#52504d" style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)"}}/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, email, ID or phone..."
              style={{width:"100%",padding:"9px 12px 9px 36px",background:"#0a0908",border:"1px solid rgba(255,255,255,0.08)",borderRadius:9,color:"#fef3e2",fontSize:13,fontFamily:"inherit",outline:"none",boxSizing:"border-box"}}/>
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {ROLES.map(r => (
              <button key={r} onClick={() => setRoleFilter(r)} style={{padding:"8px 14px",borderRadius:8,border:"1px solid",borderColor:roleFilter===r?(ROLE_COLORS[r]||"#f59e0b"):"rgba(255,255,255,0.08)",background:roleFilter===r?`${ROLE_COLORS[r]||"#f59e0b"}18`:"transparent",color:roleFilter===r?(ROLE_COLORS[r]||"#f59e0b"):"#78716c",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",textTransform:"capitalize"}}>
                {r === "all" ? "All Roles" : r}
              </button>
            ))}
          </div>
        </div>
        <div style={{fontSize:12,color:"#52504d",marginTop:10}}>
          Showing {filtered.length} of {members.length} members
        </div>
      </div>

      {/* Table */}
      <div style={{background:"#111009",border:"1px solid rgba(255,255,255,0.07)",borderRadius:14,overflow:"hidden"}}>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:700}}>
            <thead>
              <tr style={{borderBottom:"1px solid rgba(255,255,255,0.07)"}}>
                {["Member","ID","Role","Phone","Status","Actions"].map(h => (
                  <th key={h} style={{padding:"12px 16px",textAlign:"left",fontSize:11,fontWeight:700,color:"#52504d",textTransform:"uppercase",letterSpacing:"0.8px"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{padding:"32px",textAlign:"center",color:"#52504d",fontSize:13}}>Loading members...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} style={{padding:"32px",textAlign:"center",color:"#52504d",fontSize:13}}>No members found</td></tr>
              ) : filtered.map(m => {
                const rc = ROLE_COLORS[m.role] || "#78716c";
                return (
                  <tr key={m.id} style={{borderBottom:"1px solid rgba(255,255,255,0.04)"}}>

                    {/* Name + Email */}
                    <td style={{padding:"12px 16px"}}>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <div style={{width:32,height:32,borderRadius:"50%",background:`${rc}18`,border:`1px solid ${rc}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:rc,flexShrink:0}}>
                          {(m.name||"?").charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{fontSize:13,fontWeight:600,color:"#fef3e2"}}>{m.name||"—"}</div>
                          <div style={{fontSize:11,color:"#52504d"}}>{m.email}</div>
                        </div>
                      </div>
                    </td>

                    {/* Member ID */}
                    <td style={{padding:"12px 16px",fontSize:12,color:"#78716c",fontFamily:"monospace"}}>{m.memberId||"—"}</td>

                    {/* Role Dropdown */}
                    <td style={{padding:"12px 16px"}}>
                      <select value={m.role||"member"} onChange={e => changeRole(m.id, e.target.value)}
                        disabled={updating === m.id + m.role}
                        style={{background:`${rc}18`,border:`1px solid ${rc}44`,color:rc,borderRadius:7,padding:"4px 8px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",textTransform:"capitalize"}}>
                        {["member","volunteer","intern","admin","superadmin"].map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </td>

                    {/* Phone */}
                    <td style={{padding:"12px 16px",fontSize:12,color:"#78716c"}}>{m.phone||"—"}</td>

                    {/* Status */}
                    <td style={{padding:"12px 16px"}}>
                      <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:50,background:m.status==="active"?"rgba(34,197,94,0.12)":"rgba(239,68,68,0.12)",color:m.status==="active"?"#22c55e":"#ef4444"}}>
                        ● {m.status||"active"}
                      </span>
                    </td>

                    {/* Actions */}
                    <td style={{padding:"12px 16px"}}>
                      <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>

                        {/* Activate / Deactivate */}
                        <button
                          onClick={() => changeStatus(m.id, m.status==="active" ? "inactive" : "active")}
                          disabled={updating === m.id}
                          style={{padding:"5px 10px",borderRadius:7,border:"1px solid",borderColor:m.status==="active"?"rgba(239,68,68,0.3)":"rgba(34,197,94,0.3)",background:"transparent",color:m.status==="active"?"#ef4444":"#22c55e",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",opacity:updating===m.id?0.5:1}}>
                          {m.status==="active" ? "Deactivate" : "Activate"}
                        </button>

                        {/* Delete — superadmin only */}
                        {isSuperAdmin && (
                          <button
                            onClick={() => handleDelete(m.id, m.name || m.email)}
                            disabled={updating === "del" + m.id}
                            title="Delete member permanently"
                            style={{padding:"5px 10px",borderRadius:7,border:"1px solid rgba(239,68,68,0.3)",background:"rgba(239,68,68,0.08)",color:"#ef4444",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:4,opacity:updating==="del"+m.id?0.5:1}}>
                            <Trash2 size={12}/> {updating==="del"+m.id ? "..." : "Delete"}
                          </button>
                        )}

                      </div>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}